// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import baseQueryWithReauth from "./requestInterceptor";
import { normalizeRoleDoc } from "utils/permissionDependencies";

// Taxonomy normalizer (single choke point): every role doc entering the app —
// the role list, a specific role, the editor's target/caller — is mapped from
// legacy names onto the target taxonomy HERE, so the editor, clamps and engine
// only ever see normalized shapes. Legacy docs disappear once the data
// migrations run; until then this keeps both shapes interoperable.
const normalizeRolePayload = (response) => {
  if (response && Array.isArray(response.payload)) {
    return { ...response, payload: response.payload.map(normalizeRoleDoc) };
  }
  if (response && response.payload && typeof response.payload === "object") {
    return { ...response, payload: normalizeRoleDoc(response.payload) };
  }
  return response;
};

// Define a service using a base URL and expected endpoints

export const rolesApi = createApi({
  reducerPath: "rolesApi",
  tagTypes: ["Roles"],
  refetchOnMountOrArgChange: true,
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getSpecificRole: builder.query({
      query: (role) => ({
        url: `roles/${role}?services=true`,
        headers: {
          "x-access-token": window.localStorage.getItem("token"),
        },
      }),
      transformResponse: normalizeRolePayload,
    }),
    getRoles: builder.query({
      query: (token) => ({
        url: "roles/",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      }),
      transformResponse: normalizeRolePayload,
      providesTags: ["Roles"],
    }),
    createRole: builder.mutation({
      query: ({ token, body }) => ({
        url: "roles/",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body,
      }),
      invalidatesTags: ["Roles"],
    }),
    editRole: builder.mutation({
      // ifMatch: the role's updatedAt as the editor loaded (or last saved) it. The server refuses a
      // save whose stamp no longer matches (409 ROLE_VERSION_CONFLICT) instead of overwriting
      // someone else's change (solution sharing plan, stages 1 and 6).
      query: ({ token, body, id, ifMatch }) => ({
        url: "roles/" + id,
        method: "PUT",
        body: body,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
          ...(ifMatch ? { "If-Match": String(ifMatch) } : {}),
        },
      }),
      invalidatesTags: ["Roles"],
    }),
    updateServices: builder.mutation({
      query: ({ token, body, id }) => ({
        url: "roles/updateServices/" + id,
        method: "PUT",
        body: body,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      }),
      // Per-solution edits change the role too — refresh the roles list so a re-opened
      // editor (and callerRole resolution) reflects the new state, not an open-time snapshot.
      invalidatesTags: ["Roles"],
    }),
    changeRole: builder.mutation({
      query: ({ token, body, id }) => ({
        url: "changerole/" + id,
        method: "PUT",
        body: body,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      }),
    }),
    // Solution sharing (plan stage 7): the roles a solution may be shared with, and the share itself.
    // Both answer 404 SHARING_OFF while sharing is off for the tenant; an older rolesMS has no such
    // routes (an HTML 404 page, which RTK Query reports as PARSING_ERROR with originalStatus 404).
    getShareTargets: builder.query({
      query: ({ token, serviceId }) => ({
        url: `roles/share-targets/${serviceId}`,
        method: "GET",
        headers: { "Content-Type": "application/json", "x-access-token": token },
      }),
      keepUnusedDataFor: 0,
    }),
    shareSolution: builder.mutation({
      query: ({ token, serviceId, body }) => ({
        url: `roles/share/${serviceId}`,
        method: "POST",
        body,
        headers: { "Content-Type": "application/json", "x-access-token": token },
      }),
      invalidatesTags: ["Roles"],
    }),
    deleteRole: builder.mutation({
      query: ({ token, id }) => ({
        url: "roles/" + id,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      }),
      invalidatesTags: ["Roles"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSpecificRoleQuery,
  useGetRolesQuery,
  useCreateRoleMutation,
  useEditRoleMutation,
  useUpdateServicesMutation,
  useDeleteRoleMutation,
  useChangeRoleMutation,
  useLazyGetShareTargetsQuery,
  useShareSolutionMutation,
} = rolesApi;
